<template>
  <div ref="wrapperRef" class="markdown-renderer">
    <VMarkdownView :content="content" mode="light" />
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import { VMarkdownView } from 'vue3-markdown'
import 'vue3-markdown/dist/vue3-markdown.css'

const props = defineProps({
  content: {
    type: String,
    default: '',
  },
})

const wrapperRef = ref(null)

function patchLinks() {
  if (!wrapperRef.value) return
  wrapperRef.value.querySelectorAll('a').forEach((link) => {
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener noreferrer')
  })
}

watch(
  () => props.content,
  async () => { await nextTick(); patchLinks() },
  { immediate: true }
)
</script>

<style scoped>
.markdown-renderer {
  line-height: 1.7;
  color: var(--text-black);
}

/* Override vue3-markdown defaults to match design system */
.markdown-renderer :deep(.markdown-body) {
  font-family: 'Roboto', sans-serif;
  font-size: 18px;
  line-height: 1.7;
  color: var(--text-black);
  background: transparent;
  padding: 0;
}

.markdown-renderer :deep(.markdown-body h1),
.markdown-renderer :deep(.markdown-body h2),
.markdown-renderer :deep(.markdown-body h3),
.markdown-renderer :deep(.markdown-body h4) {
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  color: var(--text-black);
}

.markdown-renderer :deep(.markdown-body h1) { font-size: 1.8rem; line-height: 2rem; margin-bottom: 0.5em; }
.markdown-renderer :deep(.markdown-body h2) { font-size: 1.5rem; line-height: 1.8rem; margin-bottom: 0.5em; }
.markdown-renderer :deep(.markdown-body h3) { font-size: 1.25rem; line-height: 1.5rem; margin-bottom: 0.4em; }

.markdown-renderer :deep(.markdown-body p) {
  margin-bottom: 0.75em;
}

.markdown-renderer :deep(.markdown-body a) {
  color: var(--halsa-green);
  text-decoration: underline;
}

.markdown-renderer :deep(.markdown-body a:hover) {
  color: var(--halsa-green-dark);
}

.markdown-renderer :deep(.markdown-body ul),
.markdown-renderer :deep(.markdown-body ol) {
  padding-left: 1.5em;
  margin-bottom: 0.75em;
}

.markdown-renderer :deep(.markdown-body ul) {
  list-style-type: disc;
}

.markdown-renderer :deep(.markdown-body ol) {
  list-style-type: decimal;
}

.markdown-renderer :deep(.markdown-body li) {
  margin-bottom: 0.25em;
}

.markdown-renderer :deep(.markdown-body blockquote) {
  border-left: 4px solid var(--halsa-green);
  padding-left: 1em;
  margin: 0.75em 0;
  color: var(--text-grey);
  font-style: italic;
}

.markdown-renderer :deep(.markdown-body code) {
  background: var(--gray-100);
  border: 1px solid var(--gray-200);
  border-radius: 3px;
  padding: 0.1em 0.35em;
  font-size: 0.875em;
  font-family: monospace;
}

.markdown-renderer :deep(.markdown-body pre) {
  background: var(--gray-100);
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  padding: 0.75em 1em;
  overflow-x: auto;
  margin-bottom: 0.75em;
  color: var(--text-black);
  font-size: 0.875em;
}

.markdown-renderer :deep(.markdown-body pre code) {
  background: none;
  border: none;
  padding: 0;
}

.markdown-renderer :deep(.markdown-body hr) {
  border: none;
  border-top: 2px solid var(--gray-200);
  margin: 1.25em 0;
}

.markdown-renderer :deep(.markdown-body table) {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 0.75em;
}

.markdown-renderer :deep(.markdown-body th),
.markdown-renderer :deep(.markdown-body td) {
  border: 1px solid var(--gray-200);
  padding: 0.5em 0.75em;
  text-align: left;
}

.markdown-renderer :deep(.markdown-body th) {
  background: var(--gray-100);
  font-weight: 700;
}

.markdown-renderer :deep(.markdown-body img) {
  max-width: 100%;
  height: auto;
}

.markdown-renderer :deep(.markdown-body strong) {
  font-weight: 700;
}

.markdown-renderer :deep(.markdown-body em) {
  font-style: italic;
}
</style>
